import store, {
	updateSpeaking,
	addCaptionItem,
	updateCaptionItem,
	isCaptionsActive,
	getVideoSession,
	getPublisher,
	getSession,
	getUserPrefs,
} from './store'
import { createLogger } from '@/helpers/log'
import { storage } from '@/helpers/utils'
import { getOrganization } from '@/store'

const MIN_VOLUME = 0.1
const START_TALKING_DELAY = 500
const STOP_TALKING_DELAY = 1000
const STILL_TALKING_DELAY = 1000

const log = createLogger('Meeting', true)

export const logMeetingEvent = log

export const videoResolutions = {
	FHD: { width: 1920, height: 1080 },
	HD: { width: 1280, height: 720 },
	SD: { width: 640, height: 480 },
}

export const listenForActiveSpeaker = (subscriber) => {
	if (subscriber.isAudioBlocked?.()) {
		log('audio blocked, waiting...', subscriber.id)
		return
	}
	log('listening for audio change', subscriber.id)
	let level = 0
	subscriber.on('audioLevelUpdated', ({ audioLevel }) => {
		if (level === audioLevel) return
		level = audioLevel
		onAudioLevelUpdated(subscriber, audioLevel)
	})
}

const activityBySubscriberId = {}
const onAudioLevelUpdated = (subscriber, audioLevel) => {
	let now = Date.now()
	const activity = activityBySubscriberId[subscriber.id]
	if (audioLevel >= MIN_VOLUME) {
		if (!activity) {
			// first time we've seen this user speak, so save
			// the timestamp for later reference
			activityBySubscriberId[subscriber.id] = { timestamp: now, talking: false }
			updateSpeaking(subscriber.stream.connection.id, null, false)
		} else if (activity.talking) {
			if (now - activity.timestamp > STILL_TALKING_DELAY) {
				// this speaker is still talking, alert in case the
				// previous speaker has recently stopped talking
				updateSpeaking(
					subscriber.stream.connection.id,
					subscriber.stream.id,
					true
				)
			}
			activity.timestamp = now
		} else if (now - activity.timestamp > START_TALKING_DELAY) {
			// detected audio activity for more than 1s
			// for the first time.
			activity.talking = true
			updateSpeaking(
				subscriber.stream.connection.id,
				subscriber.stream.id,
				true
			)
		}
	} else if (activity && now - activity.timestamp > STOP_TALKING_DELAY) {
		// detected low audio activity for more than 2s
		if (activity.talking) {
			updateSpeaking(subscriber.stream.connection.id, null, false)
		}
		activityBySubscriberId[subscriber.id] = null
	}
}

const VOLUME_DIFF = 5

export const watchForBrowserMicChange = async () => {
	const AudioContext = window.AudioContext || window.webkitAudioContext // Safari and old versions of Chrome
	if (!AudioContext) return false
	try {
		const stream = await navigator.mediaDevices.getUserMedia({
			audio: true,
			video: false,
		})
		const ctx = new AudioContext()
		const analyser = ctx.createAnalyser()
		const mic = ctx.createMediaStreamSource(stream)
		const node = ctx.createScriptProcessor(2048, 1, 1) // no idea what these numbers mean

		analyser.smoothingTimeConstant = 0.8 // nor this
		analyser.fftSize = 1024 // nor this

		mic.connect(analyser)
		analyser.connect(node)
		node.connect(ctx.destination)

		let previousVolume = 0

		node.onaudioprocess = function () {
			// calculate average volume based on frequency bytes
			const array = new Uint8Array(analyser.frequencyBinCount)
			analyser.getByteFrequencyData(array)
			const sum = array.reduce((sum, v) => sum + v, 0)
			const avgVolumeLevel = sum / array.length
			const volume = Math.round(avgVolumeLevel) // value 0 to 100
			if (Math.abs(volume - previousVolume) > VOLUME_DIFF) {
				// only alert if the volume has changed by at least VOLUME_DIFF
				EventBus.$emit('browserMicrophoneVolume', volume)
				previousVolume = volume
			}
		}
	} catch (e) {
		console.error(e)
	}
}

const getOTDevices = () => {
	return new Promise((resolve, reject) => {
		OT.getDevices((error, devices) => {
			if (error) {
				reject(error)
			} else {
				resolve(devices)
			}
		})
	})
}

export const getMediaDevices = async (deviceType) => {
	try {
		const prefs = getUserPrefs()
		// have to call this in order to request permission from the user
		const userMediaConfig = {
			audioSource: null,
			videoSource: null,
			resolution: prefs.hd ? videoResolutions.FHD : videoResolutions.HD,
		}
		log('OT.getUserMedia, calling with config', userMediaConfig)
		await OT.getUserMedia(userMediaConfig)
		log('OT.getUserMedia succeeded')
		// now get the list of available devices from the browser
		let devices = await getOTDevices()
		log('getMediaDevices', 'OT devices', devices, prefs)
		if (deviceType) {
			// find only devices of the give input type (audio/video)
			devices = devices.filter(
				(d) => d.kind.toLowerCase() === `${deviceType}input`.toLowerCase()
			)
		}
		// For some reason, safari does not provide a groupId for video
		// input devices, so we can't dedupe in that scenario  #safariblows
		const allHaveGroupId = devices.every((d) => d.groupId)
		// if all devices have a groupId, that means we can dedupe, so let's do it
		if (allHaveGroupId) {
			// if the system indicated a "default" device, then we want to be sure
			// not to lose that one in the deduping process
			const defaultDevice = devices.find((d) => d.deviceId === 'default')
			if (defaultDevice) devices.push(defaultDevice)
			// now dedupe by groupId so we don't end up with same device listed twice.
			const deduped = new Map(devices.map((d) => [d.groupId, d])).values()
			return Array.from(deduped)
		} else {
			return devices
		}
	} catch (e) {
		log(
			'getMediaDevices',
			'failed to acquire list of devices',
			JSON.stringify(e)
		)
		console.error(e)
		// this is likely a known tokbox error code that we can use to provide the user with details
		EventBus.$emit('tokboxError', e.name)
	}
}

export const setPreferredDevice = (deviceType, deviceId) => {
	log('setPreferredDevice', { deviceType, deviceId })
	return storage.set(`meetings_pref_device_${deviceType}`, deviceId)
}

export const getPreferredDevice = async (deviceType) => {
	log('getPreferredDevice', { deviceType })
	const deviceId = storage.get(`meetings_pref_device_${deviceType}`)
	if (!deviceId) {
		log('getPreferredDevice', `no preferred device for type: "${deviceType}"`)
		return null
	}
	log(
		'getPreferredDevice',
		`has preferred device for type "${deviceType}" with id "${deviceId}"`
	)
	const devices = await getMediaDevices(deviceType)
	if (!devices?.find) {
		log('getPreferredDevice', 'no devices found')
		return null
	}
	const preferredDevice = devices.find((d) => d.deviceId === deviceId)
	if (!preferredDevice) {
		log('getPreferredDevice', 'preferred device not found')
		return null
	}
	log(
		'getPreferredDevice',
		'preferred device found',
		preferredDevice.label,
		preferredDevice.deviceId
	)
	return preferredDevice
}

export const getInitialDevice = async (deviceType) => {
	const preferredAudioDevice = await getPreferredDevice(deviceType)
	if (preferredAudioDevice) {
		return preferredAudioDevice.deviceId
	} else {
		const devices = await getMediaDevices(deviceType)
		log('devices', devices)
		const defaultDevice = devices.find((d) => d.deviceId === 'default')
		log(
			'defaultDevice',
			defaultDevice?.deviceId || devices[0]?.deviceId || undefined
		)
		return defaultDevice?.deviceId || devices[0]?.deviceId || undefined
	}
}

export const handleCaptionReceived = ({
	caption,
	streamId,
	isFinal,
	target,
}) => {
	const isActive = isCaptionsActive()
	if (!isActive) {
		log('caption', 'ignoring because captions inactive', {
			streamId,
			isFinal,
			caption,
		})
		return
	}
	if (target?.connection?.data) {
		const user = JSON.parse(target.connection.data)
		log('caption', user.fullname, { streamId, isFinal, caption })
	} else {
		log('caption', { streamId, isFinal, caption })
	}
	const previousPartial = store.captions.partialCaptionsByStreamId[streamId]
	if (isFinal && previousPartial) {
		// this is a complete caption, so update the partial caption we had previous captured
		updateCaptionItem({ ...previousPartial, text: caption })
		store.captions.partialCaptionsByStreamId[streamId] = null
	} else if (previousPartial) {
		// this is just an update to a previous partial caption, so update the previous capture
		store.captions.partialCaptionsByStreamId[streamId] = updateCaptionItem({
			...previousPartial,
			text: caption,
		})
	} else {
		// this is a new caption we haven't seen before so generate a new item
		store.captions.partialCaptionsByStreamId[streamId] = addCaptionItem({
			streamId,
			text: caption,
		})
	}
}

export const syncPublisherCaptions = () => {
	log('publisher', 'syncing captions state...')
	const org = getOrganization()
	if (!org.meetings_captions_enabled) {
		log(
			'publisher',
			'org does not have captions enabled, cannot publish captions'
		)
		return
	}
	const publisher = getPublisher()
	if (!publisher || !publisher.stream) {
		log('publisher', 'publisher not available yet')
		return
	}
	const { captions_enabled } = getVideoSession()
	// if we should be publishing but we aren't, then start publishing
	if (captions_enabled && !publisher.isPublishingCaptions) {
		publisher.publishCaptions(true)
		publisher.isPublishingCaptions = true
		log('publisher', 'now publishing captions')
		// if we shouldn't be publishing but we are, then stop publishing
	} else if (!captions_enabled && publisher.isPublishingCaptions) {
		publisher.publishCaptions(false)
		publisher.isPublishingCaptions = false
		log('publisher', 'no longer publishing captions')
	} else {
		// otherwise, we're good to continue as-is
		log('publisher', 'no need to change captions state', {
			captions_enabled,
			isPublishing: publisher.isPublishingCaptions,
		})
	}
}

export const createPublisherCaptionsSubscriber = (publisher) => {
	const org = getOrganization()
	if (!org.meetings_captions_enabled) return
	publisher = publisher || getPublisher()
	if (!publisher || publisher.captionsSubscriber) return
	if (!publisher.stream) {
		log('publisher', 'waiting for stream to add captions subscriber...')
		return setTimeout(() => createPublisherCaptionsSubscriber(publisher), 1000)
	}
	try {
		log('publisher', 'adding captions subscriber...')
		const session = getSession()
		// following docs here: https://tokbox.com/developer/guides/live-captions/js/
		publisher.captionsSubscriber = session.subscribe(
			publisher.stream,
			document.createElement('div'),
			{ audioVolume: 0 },
			(err) => {
				if (err) {
					console.error('Cannot subscribe to own publisher', err)
					log('publisher', 'captions subscriber error', err)
					window.prontoAlert.error(err)
				} else {
					publisher.captionsSubscriber.subscribeToCaptions(true)
					log('publisher', 'captions subscriber success!')
				}
			}
		)
		publisher.captionsSubscriber.on('captionReceived', handleCaptionReceived)
		log('publisher', 'captions subscriber added')
	} catch (e) {
		console.error(e)
	}
}
